import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logInfo, logError } from "../../utils/logger";
import { Formik, Form, Field } from "formik";
import { toast } from 'react-toastify';
import {
  addPaymentMethodToClinic,
  removePaymentMethodFromClinic,
  copyPaymentMethodsBetweenClinics,
  fetchClinics,
  fetchClinicById,
  updateClinic
} from "../../store/thunks/clinicThunk";
import { 
  PlusIcon, 
  TrashIcon, 
  DocumentDuplicateIcon,
  ChevronUpDownIcon,
  CheckIcon 
} from "@heroicons/react/20/solid";
import { Listbox, Transition, Dialog } from "@headlessui/react";

function ClinicDetails({ clinic, onUpdateClinic, editMode = false }) {
  const dispatch = useDispatch();
  const { clinics = [] } = useSelector((state) => state.clinics);
  const [saveStatus, setSaveStatus] = useState('initial');
  
  // Payment method management state
  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false);
  const [showCopyPaymentMethodsModal, setShowCopyPaymentMethodsModal] = useState(false);
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    englishDescription: ''
  });
  const [copySettings, setCopySettings] = useState({
    sourceClinic: null,
    replaceExisting: false
  });
  const [operationLoading, setOperationLoading] = useState(false);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    paymentMethod: null
  });

  console.log('[Clinic Details] Received clinic prop:', {
    clinicId: clinic?._id,
    clinicName: clinic?.name,
    paymentMethodsCount: clinic?.paymentMethods?.length || 0
  });

  if (!clinic) {
    logInfo("No clinic provided to ClinicDetails component");
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-sm text-gray-600">No clinic selected.</div>
      </div>
    );
  }

  // Use the clinic prop directly - no Redux state confusion
  const currentClinic = clinic;

  logInfo("Rendering clinic details", {
    clinicId: currentClinic._id,
    clinicName: currentClinic.name,
  });

  const formatHours = (hours) => {
    if (!hours || !hours.open || !hours.close) {
      return "Closed";
    }
    return `${hours.open} - ${hours.close}`;
  };

  // Payment method management functions
  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    
    if (!paymentMethodForm.englishDescription || !paymentMethodForm.englishDescription.trim()) {
      toast.warning('Payment method description is required');
      return;
    }
    
    if (paymentMethodForm.englishDescription.length > 31) {
      toast.warning('Description must be 31 characters or less');
      return;
    }
    
    setOperationLoading(true);
    try {
      const updatedClinic = await dispatch(addPaymentMethodToClinic({
        clinicId: currentClinic._id,
        paymentMethodData: paymentMethodForm
      })).unwrap();
      
      // Use the updated clinic from the result
      onUpdateClinic(updatedClinic);
      
      toast.success('Payment method added successfully');
      setShowAddPaymentMethodModal(false);
      setPaymentMethodForm({ englishDescription: '' });
    } catch (error) {
      toast.error(`Failed to add payment method: ${error}`);
    } finally {
      setOperationLoading(false);
    }
  };
  
  const handleRemovePaymentMethod = (paymentMethod) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Payment Method',
      message: `Are you sure you want to remove "${paymentMethod.englishDescription}"? This action cannot be undone.`,
      paymentMethod,
      onConfirm: async () => {
        setOperationLoading(true);
        try {
          const updatedClinic = await dispatch(removePaymentMethodFromClinic({
            clinicId: currentClinic._id,
            paymentMethodId: paymentMethod._id
          })).unwrap();
          
          // Use the updated clinic from the result
          onUpdateClinic(updatedClinic);
          
          toast.success('Payment method removed successfully');
        } catch (error) {
          toast.error(`Failed to remove payment method: ${error}`);
        } finally {
          setOperationLoading(false);
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, paymentMethod: null });
        }
      }
    });
  };
  
  const handleCopyPaymentMethods = async (e) => {
    e.preventDefault();
    
    if (!copySettings.sourceClinic) {
      toast.warning('Please select a source clinic to copy from');
      return;
    }
    
    if (copySettings.sourceClinic._id === currentClinic._id) {
      toast.warning('Cannot copy payment methods from the same clinic');
      return;
    }
    
    const confirmMessage = copySettings.replaceExisting 
      ? `This will replace all existing payment methods in "${currentClinic.name}" with methods from "${copySettings.sourceClinic.name}".`
      : `This will add payment methods from "${copySettings.sourceClinic.name}" to "${currentClinic.name}" (duplicates will be skipped).`;
    
    setConfirmDialog({
      open: true,
      title: 'Copy Payment Methods',
      message: confirmMessage + ' This action cannot be undone.',
      onConfirm: async () => {
        await performCopyOperation();
      }
    });
  };
  
  const performCopyOperation = async () => {
    setOperationLoading(true);
    try {
      const result = await dispatch(copyPaymentMethodsBetweenClinics({
        targetClinicId: currentClinic._id,
        sourceClinicId: copySettings.sourceClinic._id,
        replaceExisting: copySettings.replaceExisting
      })).unwrap();
      
      // Use the updated clinic from the result
      onUpdateClinic(result.clinic);
      
      toast.success(
        `Payment methods copied successfully. ${result.copiedCount} methods copied` +
        (result.skippedCount > 0 ? `, ${result.skippedCount} duplicates skipped` : '')
      );
      setShowCopyPaymentMethodsModal(false);
      setCopySettings({ sourceClinic: null, replaceExisting: false });
    } catch (error) {
      toast.error(`Failed to copy payment methods: ${error}`);
    } finally {
      setOperationLoading(false);
      setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, paymentMethod: null });
    }
  };

  const handleSubmit = async (values) => {
    setSaveStatus('saving');
    try {
      console.log('[Clinic Details] Form submission values:', values);
      console.log('[Clinic Details] Current clinic payment methods:', currentClinic.paymentMethods);

      // Validate invoicePrefix before submitting
      if (!values.invoicePrefix || values.invoicePrefix.length !== 4 || !/^[A-Z]{4}$/.test(values.invoicePrefix)) {
        toast.error('Invoice prefix must be exactly 4 uppercase letters');
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('initial'), 2000);
        return;
      }

      // The payment methods should be submitted as-is since they're already complete objects
      const updatedValues = {
        ...values,
        // Keep payment methods as they are - they should already be the complete objects from the database
      };

      console.log('[Clinic Details] Submitting updated values:', updatedValues);

      // Dispatch the updateClinic thunk
      const resultAction = await dispatch(updateClinic({
        id: currentClinic._id,
        clinicData: updatedValues
      }));

      if (updateClinic.fulfilled.match(resultAction)) {
        const updatedClinic = resultAction.payload;
        setSaveStatus('saved');
        toast.success('Clinic updated successfully');

        // Pass the updated clinic to the parent
        onUpdateClinic(updatedClinic);

        setTimeout(() => setSaveStatus('initial'), 2000);
      } else {
        // Handle the error from the thunk
        const errorMessage = resultAction.payload || resultAction.error?.message || 'Failed to update clinic';
        toast.error(errorMessage);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('initial'), 2000);
      }
    } catch (error) {
      console.error('[Clinic Details] Form submission error:', error);
      toast.error(`Error: ${error.message || 'Failed to update clinic'}`);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('initial'), 2000);
    }
  };
  
  // Get other clinics for copying (exclude current clinic)
  const otherClinics = clinics.filter(c => c._id !== currentClinic._id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Clinic Details</h2>
          <p className="text-sm text-gray-500 mt-1">Basic clinic information and settings</p>
        </div>
      </div>

      <Formik
        initialValues={currentClinic}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Clinic Name *
                  </label>
                  {editMode ? (
                    <Field
                      name="name"
                      type="text"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.name}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  {editMode ? (
                    <Field
                      name="email"
                      type="email"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.email}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  {editMode ? (
                    <Field
                      name="phoneNumber"
                      type="tel"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.phoneNumber}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  {editMode ? (
                    <Field
                      name="website"
                      type="url"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.website || 'Not specified'}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="invoicePrefix" className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Prefix * (4 uppercase letters)
                  </label>
                  {editMode ? (
                    <>
                      <Field
                        name="invoicePrefix"
                        type="text"
                        maxLength={4}
                        placeholder="ABCD"
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent uppercase"
                        style={{textTransform: 'uppercase'}}
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Recommended: First 2 letters = Clinic name, Last 2 letters = Location (e.g., ECTO for "Example Clinic Toronto")
                      </p>
                    </>
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.invoicePrefix || <span className="text-red-600">⚠️ Not set - required for invoicing</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Address</h3>
              {editMode ? (
                <div className="space-y-3">
                  <Field
                    name="address.street"
                    type="text"
                    placeholder="Street Address"
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Field
                      name="address.city"
                      type="text"
                      placeholder="City"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <Field
                      name="address.province"
                      type="text"
                      placeholder="Province"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <Field
                      name="address.postalCode"
                      type="text"
                      placeholder="Postal Code"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <Field
                      name="address.country"
                      type="text"
                      placeholder="Country"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                  {values.address?.street && (
                    <>
                      {values.address.street}<br />
                      {values.address.city}, {values.address.province} {values.address.postalCode}<br />
                      {values.address.country}
                    </>
                  ) || 'No address specified'}
                </div>
              )}
            </div>

            {/* Hours of Operation */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Hours of Operation</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(values.hoursOfOperation || {}).map(([day, hours]) => (
                  <div key={day} className="flex items-center space-x-2">
                    <span className="w-20 text-sm font-medium text-gray-700">{day}:</span>
                    {editMode ? (
                      <div className="flex items-center space-x-1 flex-1">
                        <Field
                          name={`hoursOfOperation.${day}.open`}
                          type="time"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <span className="text-gray-500">-</span>
                        <Field
                          name={`hoursOfOperation.${day}.close`}
                          type="time"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-gray-900">{formatHours(hours)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">
                  Payment Methods
                  <span className="ml-2 text-xs text-gray-500">
                    ({currentClinic.paymentMethods?.length || 0} configured)
                  </span>
                </h3>
                {editMode && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCopyPaymentMethodsModal(true)}
                      className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                      title="Copy from another clinic"
                    >
                      <DocumentDuplicateIcon className="h-3 w-3" />
                      Copy
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddPaymentMethodModal(true)}
                      className="inline-flex items-center gap-1.5 rounded border border-indigo-600 bg-indigo-600 px-2 py-1 text-xs font-medium text-white shadow-sm transition-colors hover:bg-indigo-500"
                    >
                      <PlusIcon className="h-3 w-3" />
                      Add
                    </button>
                  </div>
                )}
              </div>
              
              {editMode ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Manage payment methods for this clinic:
                  </p>
                  {currentClinic.paymentMethods?.length > 0 ? (
                    <div className="space-y-2">
                      {currentClinic.paymentMethods.map((method, index) => (
                        <div key={method._id || method.value || index} 
                             className="flex items-center justify-between p-3 border border-gray-200 rounded-sm bg-gray-50">
                          <span className="text-sm font-medium text-gray-900">{method.englishDescription}</span>
                          <button
                            type="button"
                            onClick={() => handleRemovePaymentMethod(method)}
                            disabled={operationLoading}
                            className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50 disabled:opacity-50"
                          >
                            <TrashIcon className="h-3 w-3" />
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 border border-gray-200 rounded-sm bg-gray-50">
                      <p className="text-sm text-gray-500">No payment methods configured.</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Add" to create a new payment method.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                  {currentClinic.paymentMethods?.length > 0 ? (
                    <div className="space-y-1">
                      {currentClinic.paymentMethods.map((method, index) => (
                        <div key={method._id || method.value || index}>
                          <span>{method.englishDescription}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-amber-600">No payment methods configured</span>
                  )}
                </div>
              )}
            </div>

            {/* Save Button - only show when in edit mode */}
            {editMode && (
              <div className="flex justify-end pt-4 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={saveStatus === 'saving'}
                  className={`inline-flex justify-center px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                    saveStatus === 'saving'
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : saveStatus === 'saved'
                      ? 'bg-green-600 text-white'
                      : saveStatus === 'error'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {saveStatus === 'saving' ? 'Saving...' :
                    saveStatus === 'saved' ? 'Saved!' :
                    saveStatus === 'error' ? 'Error' : 'Save Changes'}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>

      {/* Add Payment Method Modal */}
      <Transition appear show={showAddPaymentMethodModal} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowAddPaymentMethodModal(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden bg-white p-6 align-middle shadow-xl transition-all rounded-sm">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Add Payment Method
                  </Dialog.Title>
                  
                  <form onSubmit={handleAddPaymentMethod} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method Description * (max 31 characters)
                      </label>
                      <input
                        type="text"
                        value={paymentMethodForm.englishDescription}
                        onChange={(e) => setPaymentMethodForm(prev => ({ ...prev, englishDescription: e.target.value }))}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="e.g., Visa - In Person, Cash, Interac Debit"
                        maxLength={31}
                        required
                        autoFocus
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {paymentMethodForm.englishDescription.length}/31 characters
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddPaymentMethodModal(false);
                          setPaymentMethodForm({ englishDescription: '' });
                        }}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={operationLoading}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {operationLoading ? 'Adding...' : 'Add Payment Method'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Copy Payment Methods Modal */}
      <Transition appear show={showCopyPaymentMethodsModal} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowCopyPaymentMethodsModal(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden bg-white p-6 align-middle shadow-xl transition-all rounded-sm">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Copy Payment Methods
                  </Dialog.Title>
                  
                  <form onSubmit={handleCopyPaymentMethods} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Copy payment methods from:
                      </label>
                      <Listbox value={copySettings.sourceClinic} onChange={(clinic) => setCopySettings(prev => ({ ...prev, sourceClinic: clinic }))}>
                        <div className="relative">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-sm border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                            <span className="block truncate">
                              {copySettings.sourceClinic ? copySettings.sourceClinic.name : "Select a clinic..."}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={React.Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-sm border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                              {otherClinics.map((clinic) => (
                                <Listbox.Option
                                  key={clinic._id}
                                  value={clinic}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                      active ? "bg-indigo-50 text-indigo-900" : "text-gray-700"
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                        {clinic.name} ({clinic.paymentMethods?.length || 0} methods)
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                          <CheckIcon className="h-4 w-4" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                              {otherClinics.length === 0 && (
                                <div className="py-2 px-3 text-gray-500 text-sm">
                                  No other clinics available
                                </div>
                              )}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>
                    
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={copySettings.replaceExisting}
                          onChange={(e) => setCopySettings(prev => ({ ...prev, replaceExisting: e.target.checked }))}
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Replace existing payment methods
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {copySettings.replaceExisting 
                          ? "This will remove all current payment methods and replace them with the copied ones."
                          : "This will add new payment methods without removing existing ones. Duplicates will be skipped."
                        }
                      </p>
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCopyPaymentMethodsModal(false);
                          setCopySettings({ sourceClinic: null, replaceExisting: false });
                        }}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={operationLoading || !copySettings.sourceClinic}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {operationLoading ? 'Copying...' : 'Copy Payment Methods'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Custom Confirmation Dialog */}
      <Transition appear show={confirmDialog.open} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden bg-white p-6 align-middle shadow-xl transition-all rounded-sm">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    {confirmDialog.title}
                  </Dialog.Title>
                  
                  <p className="text-sm text-gray-600 mb-6">
                    {confirmDialog.message}
                  </p>
                  
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmDialog.onConfirm}
                      disabled={operationLoading}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      {operationLoading ? 'Processing...' : 'Confirm'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default ClinicDetails;